import { Progress } from "@/shared/components/ui/progress";
import type {
  AprendizPrograma,
  Programa,
} from "../interfaces/Programas.interface";
import { formatearFechaISOaDDMMYYYY } from "@/shared/utils/utils";
import { getIntentosInfo } from "@/shared/utils/intentos";
import { IntentosBadge } from "@/shared/components/IntentosBadge";
import {
  BookOpen,
  ChevronDown,
  FileText,
  Download,
  ArrowRight,
  RefreshCcw,
  Lock,
  History,
  Loader2,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Link } from "react-router";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { downloadPrivateObject } from "@retro-ia/shared/upload";
import { useState } from "react";
import { useDescargarRetroPdf } from "@/hooks/reportes/useDescargarRetroPdf";

export default function Programa({
  aprendizPrograma,
}: {
  aprendizPrograma: AprendizPrograma;
}) {
  const { programa, progreso } = aprendizPrograma;
  const fechaActual = new Date();
  const [isOpen, setIsOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const {
    mutate: descargarHistorial,
    isPending: descargandoHistorial,
    variables: historialSolicitado,
  } = useDescargarRetroPdf();

  const handleDownloadAdjunto = async (adjunto: {
    llave: string;
    nombre: string;
    url?: string;
  }) => {
    if (adjunto.llave) {
      setDownloadingKey(adjunto.llave);
      try {
        const { url } = await downloadPrivateObject({ key: adjunto.llave });
        window.open(url, "_blank");
      } finally {
        setDownloadingKey(null);
      }
    } else if (adjunto.url) {
      window.open(adjunto.url, "_blank");
    }
  };

  // Verificar si el programa está en fechas válidas y está activo
  const fechaInicio = new Date(programa.fechaInicio);
  const fechaFin = new Date(programa.fechaFin);
  const enFechasValidas = fechaActual >= fechaInicio && fechaActual <= fechaFin;
  const programaActivo =
    enFechasValidas && programa.isActivo && aprendizPrograma.isActivo;

  // Filtrar actividades asignadas al aprendiz y que no han terminado
  const actividadesActivas = programa.actividadPrograma.filter((actividad) => {
    // Solo mostrar actividades asignadas al aprendiz (isAsignado=true)
    if (!actividad.actividadAprendiz || actividad.actividadAprendiz.length === 0) {
      return false;
    }
    if (!actividad.fechaFin) return true; // Si no tiene fechaFin, se muestra
    const fechaFin = new Date(actividad.fechaFin);
    return fechaFin >= fechaActual;
  });

  const handleClick = () => {
    if (programaActivo) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <section className="px-4 md:px-8 mb-6">
      {/* Card del Programa - Acordeón */}
      <div
        className={`rounded-xl shadow-md border mb-4 ${
          programaActivo
            ? "bg-white dark:bg-[var(--card-background)] border-[var(--secondary)]"
            : "bg-gray-100 dark:bg-[var(--background-muted)] border-gray-300 dark:border-gray-700"
        }`}
      >
        <button
          onClick={handleClick}
          className={`w-full p-4 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4 transition-colors rounded-xl ${
            programaActivo
              ? "hover:bg-gradient-to-r hover:from-[var(--primary)]/5 hover:to-[var(--secondary)]/8 cursor-pointer"
              : "cursor-not-allowed"
          }`}
        >
          <div className="flex items-center gap-3 md:gap-4 w-full sm:flex-1">
            <div
              className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center ${
                programaActivo
                  ? "bg-[var(--primary)]/10 dark:bg-[var(--primary)]/20"
                  : "bg-gray-300 dark:bg-gray-700"
              }`}
            >
              <BookOpen
                className={
                  programaActivo
                    ? "text-[var(--primary)]"
                    : "text-gray-500 dark:text-gray-400"
                }
                size={20}
              />
            </div>

            <div className="flex-1 text-left">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h2
                  className={`font-bold text-base md:text-xl ${
                    programaActivo
                      ? "text-[var(--foreground)]"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {programa.nombre}
                </h2>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    programaActivo
                      ? "bg-[var(--background-muted)] text-[var(--primary)] dark:bg-[var(--secondary)]/20"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                  }`}
                >
                  {actividadesActivas.length}{" "}
                  {actividadesActivas.length === 1
                    ? "actividad"
                    : "actividades"}
                </span>
              </div>
              <p
                className={`text-xs md:text-sm ${
                  programaActivo
                    ? "text-[var(--foreground)] opacity-70"
                    : "text-gray-400 dark:text-gray-500"
                }`}
              >
                {formatearFechaISOaDDMMYYYY(programa.fechaInicio)} -{" "}
                {formatearFechaISOaDDMMYYYY(programa.fechaFin)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-4 w-full sm:w-auto">
            <div className="text-left sm:text-right flex-1 sm:flex-initial sm:min-w-[120px]">
              <Progress value={progreso} className="mb-1 h-2" />
              <p
                className={`text-xs md:text-sm font-semibold ${
                  programaActivo
                    ? "text-[var(--primary)]"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {progreso}% completado
              </p>
            </div>
            {programaActivo ? (
              <ChevronDown
                className={`text-[var(--secondary)] transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
                size={20}
              />
            ) : (
              <Lock
                className="text-[var(--secondary)] flex-shrink-0"
                size={20}
              />
            )}
          </div>
        </button>
      </div>

      {/* Contenido expandible - Actividades */}
      {programaActivo && (
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isOpen ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex gap-2 md:gap-4">
            {/* Línea punteada morada continua */}
            <div className="w-8 md:w-16 flex justify-center">
              <div className="w-0.5 border-l-2 border-dashed border-[var(--primary)]"></div>
            </div>

            {/* Contenedor de actividades */}
            <div className="flex-1 space-y-3 md:space-y-4">
              {actividadesActivas.map((actividad, index) => {
                // Validar si la actividad está activa y en fechas válidas
                const fechaActualTime = fechaActual.getTime();
                const actividadFechaInicio = actividad.fechaInicio
                  ? new Date(actividad.fechaInicio).getTime()
                  : 0;
                const actividadFechaFin = actividad.fechaFin
                  ? new Date(actividad.fechaFin).getTime()
                  : Infinity;

                const enFechasValidas =
                  fechaActualTime >= actividadFechaInicio &&
                  fechaActualTime <= actividadFechaFin;

                const actividadActiva = enFechasValidas && actividad.isActivo;

                const tienePdf =
                  !!actividad.archivoLimpio?.llave ||
                  !!actividad.archivoLimpio?.url;
                const tieneWord =
                  !!actividad.archivoWord?.llave ||
                  !!actividad.archivoWord?.url;
                const puedeDescargar = actividadActiva && tieneWord;
                const puedeAplicar = actividadActiva && tienePdf;
                const intentosInfo = getIntentosInfo({
                  intentos: actividad.actividadAprendiz[0]?.intentos,
                  intentosExtras: actividad.actividadAprendiz[0]?.intentosExtras,
                  limiteIntentos: programa.limiteIntentos,
                });

                return (
                  <div
                    key={index}
                    className={`rounded-lg border p-3 md:p-4 ${
                      actividadActiva
                        ? "bg-gradient-to-r from-[var(--primary)]/10 to-[var(--secondary)]/10 border-[var(--secondary)]/40"
                        : "bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 border-gray-300 dark:border-gray-700"
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Icono de archivo */}
                        <div
                          className={`flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center ${
                            actividadActiva
                              ? "bg-[var(--primary)]/10 dark:bg-[var(--primary)]/20"
                              : "bg-gray-200 dark:bg-gray-700"
                          }`}
                        >
                          <FileText
                            className={
                              actividadActiva
                                ? "text-[var(--primary)]"
                                : "text-gray-500 dark:text-gray-400"
                            }
                            size={18}
                          />
                        </div>

                        {/* Información de la actividad */}
                        <div className="flex-1 min-w-0">
                          <h3
                            className={`font-bold text-sm md:text-base mb-1 ${
                              actividadActiva
                                ? "text-[var(--foreground)]"
                                : "text-gray-500 dark:text-gray-400"
                            }`}
                          >
                            {actividad.nombre}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
                            <IntentosBadge
                              info={intentosInfo}
                              className={
                                actividadActiva
                                  ? "text-[var(--foreground)] opacity-70"
                                  : "text-gray-400 dark:text-gray-500"
                              }
                            />
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                tienePdf
                                  ? actividadActiva
                                    ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200"
                                    : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                              }`}
                            >
                              {tienePdf ? "Disponible" : "Sin archivo"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Botones de acción */}
                      <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-shrink-0">
                        <TooltipProvider>
                          {puedeDescargar ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 h-8 md:h-9 w-10 md:w-11 px-0"
                                  disabled={
                                    downloadingKey ===
                                    actividad.archivoWord!.llave
                                  }
                                  onClick={() =>
                                    handleDownloadAdjunto(
                                      actividad.archivoWord!,
                                    )
                                  }
                                >
                                  <Download
                                    size={16}
                                    className="md:w-5 md:h-5"
                                    strokeWidth={2.5}
                                  />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Descargar archivo</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex-shrink-0">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled
                                    className="gap-1 h-8 md:h-9 w-10 md:w-11 px-0"
                                  >
                                    <Download
                                      size={16}
                                      className="md:w-5 md:h-5"
                                      strokeWidth={2.5}
                                    />
                                  </Button>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {!tieneWord
                                    ? "No hay archivo para descargar"
                                    : "Actividad no disponible"}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {intentosInfo.usados > 0 &&
                            actividad.actividadAprendiz[0]?.id && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1 h-8 md:h-9 w-10 md:w-11 px-0"
                                    aria-label="Descargar historial de retroalimentación"
                                    disabled={descargandoHistorial}
                                    onClick={() =>
                                      descargarHistorial({
                                        idActividadAprendiz:
                                          actividad.actividadAprendiz[0].id,
                                        alcance: "historial",
                                        actividadNombre: actividad.nombre,
                                      })
                                    }
                                  >
                                    {descargandoHistorial &&
                                    historialSolicitado?.idActividadAprendiz ===
                                      actividad.actividadAprendiz[0].id ? (
                                      <Loader2
                                        size={16}
                                        className="md:w-5 md:h-5 animate-spin"
                                      />
                                    ) : (
                                      <History
                                        size={16}
                                        className="md:w-5 md:h-5"
                                        strokeWidth={2.5}
                                      />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Descargar historial de retroalimentación (PDF)</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          {intentosInfo.usados > 0 && !intentosInfo.agotados ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex-1 sm:flex-initial">
                                  <Link
                                    to={
                                      puedeAplicar
                                        ? `/actividad/${actividad?.id}`
                                        : "#"
                                    }
                                    state={{
                                      cerebro: programa.cerebro,
                                      actividad,
                                      programaId: programa.id,
                                      actividadesPrograma: actividadesActivas,
                                      forceInitialView: true,
                                    }}
                                    className={`${!puedeAplicar ? "pointer-events-none" : ""}`}
                                  >
                                    <Button
                                      size="sm"
                                      disabled={!puedeAplicar}
                                      className="gap-1.5 text-xs md:text-sm h-8 md:h-9 w-full sm:w-auto px-3 md:px-4 bg-primary"
                                    >
                                      <span className="hidden sm:inline">
                                        Reintentar
                                      </span>
                                      <span className="sm:hidden">
                                        Reintentar
                                      </span>
                                      <RefreshCcw
                                        size={14}
                                        className="md:w-4 md:h-4"
                                      />
                                    </Button>
                                  </Link>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {!tienePdf
                                    ? "No hay PDF de reto para esta actividad"
                                    : !actividadActiva
                                      ? "Actividad no disponible"
                                      : "Reintentar"}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <></>
                          )}

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex-1 sm:flex-initial">
                                <Link
                                  to={
                                    puedeAplicar
                                      ? `/instrucciones-actividad`
                                      : "#"
                                  }
                                  state={{
                                    cerebro: programa.cerebro,
                                    actividad,
                                    programaId: programa.id,
                                    actividadesPrograma: actividadesActivas,
                                  }}
                                  className={`${!puedeAplicar ? "pointer-events-none" : ""}`}
                                >
                                  <Button
                                    size="sm"
                                    disabled={!puedeAplicar}
                                    className="gap-1.5 text-xs md:text-sm h-8 md:h-9 w-full sm:w-auto px-3 md:px-4 bg-primary"
                                  >
                                    {intentosInfo.usados > 0 ? (
                                      <>
                                        <span className="hidden sm:inline">
                                          Ver mi reto
                                        </span>
                                        <span className="sm:hidden">
                                          Ver reto
                                        </span>
                                        <ArrowRight
                                          size={14}
                                          className="md:w-4 md:h-4"
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <span className="hidden sm:inline">
                                          Subir mi reto
                                        </span>
                                        <span className="sm:hidden">
                                          Subir reto
                                        </span>
                                        <ArrowRight
                                          size={14}
                                          className="md:w-4 md:h-4"
                                        />
                                      </>
                                    )}
                                  </Button>
                                </Link>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {!tienePdf
                                  ? "No hay PDF de reto para esta actividad"
                                  : !actividadActiva
                                    ? "Actividad no disponible"
                                    : "Subir mi reto"}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
